/**
 * Backend API-rot för fetch(`${getApiBase()}/triggers`, …).
 *
 * I utveckling: localhost → direkt mot :3001. Öppnar du via domän/LAN-värd används
 * samma protokoll + värd + /api (reverse proxy), annars försöker browsern nå :3001 utåt.
 *
 * - dev + localhost/127.0.0.1/::1 → http://127.0.0.1:3001/api
 * - dev + annat värdnamn → <samma som sidan>/api
 * - produktion → <samma som sidan>/api
 *
 * Sätt REACT_APP_API_BASE vid build om API ligger på annan domän/subdomän.
 */

export function getApiBase() {
  const env = process.env.REACT_APP_API_BASE;
  if (env && String(env).trim()) {
    return String(env).replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;

    if (!hostname || protocol === "file:") {
      return "http://127.0.0.1:3001/api";
    }

    if (process.env.NODE_ENV === "development") {
      const isLocalhost =
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "::1" ||
        hostname === "[::1]";
      if (isLocalhost) {
        return "http://127.0.0.1:3001/api";
      }

      // Om dev körs via lokal privat IP (http://192.168.x.x:3000),
      // anropa backend direkt på :3001 (utan Nginx).
      const isPrivateIpv4 =
        /^10\./.test(hostname) ||
        /^192\.168\./.test(hostname) ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
      if (protocol === "http:" && isPrivateIpv4) {
        return `http://${hostname}:3001/api`;
      }

      // Domän/extern åtkomst i dev: gå via reverse proxy (/api).
      return `${protocol}//${hostname}/api`;
    }

    // Utanför dev: anropa aldrig :3001 i webbläsaren om API bara nås via reverse proxy på 443.
    return `${protocol}//${hostname}/api`;
  }
  return "http://127.0.0.1:3001/api";
}

const AUTH_TOKEN_KEY = "dashboard_auth_token";

export function getAuthToken() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token) {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
}

export function apiFetch(url, init = {}) {
  const headers = { ...(init.headers || {}) };
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(url, { ...init, headers });
}

/**
 * Läser fetch-svar som JSON. Tydligt fel om svaret är HTML (404-sida, index.html, m.m.).
 */
export async function parseJsonOrExplain(res) {
  const text = await res.text();
  const trimmed = text.trim();
  if (/^<[!\s]*doctype|^<\s*html/i.test(trimmed)) {
    if (/Cannot\s+[A-Z]+/i.test(trimmed)) {
      const m = trimmed.match(/Cannot\s+([A-Z]+)\s+([^\s<]+)/i);
      const method = m ? String(m[1] || "").toUpperCase() : "REQUEST";
      const path = m ? m[2] : "";
      throw new Error(
        `API saknar rutt för ${method}${path ? ` ${path}` : ""} (HTML-svar i stället för JSON). Starta om backend med senaste kod (node backend/index.js).`
      );
    }
    if (/You need to enable JavaScript|id="root"/i.test(trimmed)) {
      throw new Error(
        "Svaret var React-sidan (HTML), inte API. Kontrollera att backend körs på port 3001 och att getApiBase() pekar rätt (t.ex. http://127.0.0.1:3001/api i utveckling). Sätt REACT_APP_API_BASE om API har annan adress."
      );
    }
    throw new Error(
      "API returnerade HTML i stället för JSON. Starta backend på port 3001 eller sätt REACT_APP_API_BASE om API ligger någon annanstans."
    );
  }
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      res.ok
        ? "Ogiltigt JSON från API."
        : `HTTP ${res.status}: ${trimmed.slice(0, 160)}${trimmed.length > 160 ? "…" : ""}`
    );
  }
  if (!res.ok) {
    const msg =
      data && typeof data === "object" && data.error != null
        ? String(data.error)
        : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}
